<template>
  <IntercomLayout>
    <!-- Topbar -->
    <template #topbar>
      <IntercomTopbar
        :show-search="false"
        app-name="OnePlaceHQ"
        product-name="Billing"
        :business-name="businessName"
        :user="user"
        @notifications="handleNotifications"
        @settings="handleSettings"
        @logout="handleLogout"
      />
    </template>

    <!-- Sidebar -->
    <template #sidebar>
      <IntercomSidebar :nav-sections="navSections" />
    </template>

    <!-- Main Content -->
    <div class="invoices-view">
      <!-- Page Header -->
      <div class="page-header">
        <div>
          <h1 class="page-title">{{ $t('invoices.title') }}</h1>
          <p class="page-subtitle">Manage your sales invoices</p>
        </div>
        <IntercomButton variant="primary" @click="showCreateModal = true">
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <line x1="12" y1="5" x2="12" y2="19" />
            <line x1="5" y1="12" x2="19" y2="12" />
          </svg>
          <span>{{ $t('invoices.createInvoice') }}</span>
          <kbd class="shortcut-hint">N</kbd>
        </IntercomButton>
      </div>

      <!-- Search Bar -->
      <div class="search-bar">
        <div class="search-input-wrapper">
          <svg class="search-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            ref="searchInputRef"
            v-model="searchQuery"
            type="text"
            class="search-input"
            :placeholder="$t('common.search') + '...'"
            @input="handleSearchInput"
          />
          <kbd v-if="!searchQuery" class="search-shortcut-hint">/</kbd>
          <button v-if="searchQuery" class="search-clear" @click="clearSearch">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
      </div>

      <!-- Sale Type Filter -->
      <div class="filters-row">
        <IntercomFilterButton
          :active="saleTypeFilter === ''"
          @click="saleTypeFilter = ''"
        >All</IntercomFilterButton>
        <IntercomFilterButton
          :active="saleTypeFilter === 'cash'"
          @click="saleTypeFilter = 'cash'"
        >Cash</IntercomFilterButton>
        <IntercomFilterButton
          :active="saleTypeFilter === 'credit'"
          @click="saleTypeFilter = 'credit'"
        >Credit</IntercomFilterButton>
      </div>

      <!-- Invoices Table -->
      <IntercomCard>
        <IntercomTable
          :columns="columns"
          :data="invoices"
          :loading="isLoading"
          :sort-by="sortBy"
          :sort-order="sortOrder"
          :selected-index="selectedIndex"
          hoverable
          clickable
          @row-click="handleView"
          @sort="handleSort"
        >
          <template #cell-invoice_date="{ value }">
            {{ formatDate(value) }}
          </template>

          <template #cell-payment_status="{ value, row }">
            <div class="status-cell">
              <IntercomBadge :variant="getPaymentStatusBadge(value)">
                {{ $t(`invoices.status_${value}`) }}
              </IntercomBadge>
              <span :class="['sale-type-badge', row.sale_type || 'cash']">
                {{ row.sale_type === 'credit' ? 'Credit' : 'Cash' }}
              </span>
            </div>
          </template>

          <template #cell-total_amount="{ row }">
            <div class="amount-cell">
              <div class="total">Total: ₹{{ getTotal(row).toFixed(2) }}</div>
              <div v-if="getPaid(row) > 0" class="paid">Paid: ₹{{ getPaid(row).toFixed(2) }}</div>
              <div v-if="getDue(row) > 0" class="due">Due: ₹{{ getDue(row).toFixed(2) }}</div>
            </div>
          </template>

          <template #cell-actions="{ row }">
            <div class="action-buttons">
              <IntercomButton size="sm" variant="ghost" @click.stop="handleView(row)">
                View
              </IntercomButton>
              <IntercomButton
                v-if="!row.voided_at"
                size="sm"
                variant="ghost"
                @click.stop="handleEdit(row)"
              >
                Edit
              </IntercomButton>
              <IntercomButton size="sm" variant="ghost" @click.stop="handlePrint(row)">
                {{ $t('invoices.print') }}
              </IntercomButton>
              <IntercomButton size="sm" variant="ghost" @click.stop="handleDownloadPdf(row)">
                Download
              </IntercomButton>
              <IntercomButton
                v-if="getDue(row) > 0"
                size="sm"
                variant="primary"
                @click.stop="handleRecordPayment(row)"
              >
                {{ $t('invoices.recordPayment') }}
              </IntercomButton>
            </div>
          </template>
        </IntercomTable>
      </IntercomCard>

      <IntercomPagination
        :offset="offset"
        :total="total"
        :limit="limit"
        @update:offset="(v) => { offset = v; fetch() }"
      />

      <!-- Create Invoice Modal -->
      <InvoiceForm
        v-model:show="showCreateModal"
        @success="handleFormSuccess"
      />

      <!-- Edit Invoice Modal -->
      <InvoiceForm
        v-if="editInvoice"
        v-model:show="showEditModal"
        :invoice="editInvoice"
        @success="handleEditSuccess"
      />

      <!-- Payment Modal -->
      <PaymentForm
        v-model:show="showPaymentModal"
        :invoice="selectedInvoice"
        @success="handlePaymentSuccess"
      />

      <!-- Print Modal -->
      <IntercomModal
        v-model:show="showPrintModal"
        title="Print Invoice"
        width="800px"
      >
        <div class="print-preview" :class="{ 'preview-a5': paperSize === 'A5' }">
          <!-- Show Tax Invoice for GST businesses -->
          <TaxInvoicePrint v-if="isGSTBusiness && printData" :invoice-data="printData" :paper-size="paperSize" />

          <!-- Show Cash Memo for non-GST businesses -->
          <CashMemoPrint v-if="!isGSTBusiness && printData" :invoice-data="printData" :paper-size="paperSize" />
        </div>

        <template #footer>
          <div class="print-modal-footer">
            <p class="print-hint">Tip: In the print dialog, enable "Fit to printable area" for best results</p>
            <div class="print-modal-actions">
              <IntercomButton variant="secondary" @click="showPrintModal = false">
                Close
              </IntercomButton>
              <IntercomButton variant="secondary" @click="handleDownloadFromPreview">
                Download PDF
              </IntercomButton>
              <IntercomButton variant="primary" @click="handlePrintFromPreview">
                Print
              </IntercomButton>
            </div>
          </div>
        </template>
      </IntercomModal>
    </div>
  </IntercomLayout>
</template>

<script setup>
import { ref, watch, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useAuth } from '@shared/composables/useAuth.js'
import { usePrint } from '@shared/composables/usePrint.js'
import { usePaginatedList } from '@shared/composables/usePaginatedList.js'
import { usePageShortcuts } from '@shared/composables/usePageShortcuts.js'
import { useBilling } from '../composables/useBilling.js'
import {
  IntercomLayout,
  IntercomTopbar,
  IntercomSidebar,
  IntercomButton,
  IntercomCard,
  IntercomTable,
  IntercomBadge,
  IntercomModal,
  IntercomPagination,
  IntercomFilterButton,
} from '@shared/components/intercom'
import InvoiceForm from '../components/InvoiceForm.vue'
import PaymentForm from '../components/PaymentForm.vue'
import TaxInvoicePrint from '../components/TaxInvoicePrint.vue'
import CashMemoPrint from '../components/CashMemoPrint.vue'
import { billingNavSections } from '../config/navigation.js'
import { flattenPrintData, formatDate } from '../utils/billingUtils.js'

const { t } = useI18n()
const router = useRouter()
const route = useRoute()
const { user, logout } = useAuth()
const { listInvoices, getInvoice, getInvoicePrint, businessName, fetchBusinessName } = useBilling()

const navSections = billingNavSections

// State
const {
  items: invoices,
  total,
  loading: isLoading,
  offset,
  limit,
  fetch,
  resetAndFetch,
} = usePaginatedList((params) => listInvoices(params))

const saleTypeFilter = ref('')
const searchInputRef = ref(null)

// Keyboard shortcuts for list view
const { selectedIndex } = usePageShortcuts({
  items: invoices,
  onNew: () => { showCreateModal.value = true },
  onOpen: (item) => handleView(item),
  onEdit: (item) => handleEdit(item),
  canEdit: (item) => !item.voided_at,
  searchRef: searchInputRef,
})

const showCreateModal = ref(false)
const showEditModal = ref(false)
const showPaymentModal = ref(false)
const showPrintModal = ref(false)
const selectedInvoice = ref(null)
const editInvoice = ref(null)
const printData = ref(null)
const printInvoiceId = ref(null)
const isGSTBusiness = ref(true) // Will be determined from business settings
const sortBy = ref('invoice_date')
const sortOrder = ref('desc')

// Table columns
const columns = [
  { key: 'invoice_number', title: t('invoices.invoiceNumber'), width: '140px', sortable: true, sortKey: 'invoice_number' },
  { key: 'customer_name', title: t('invoices.customer'), width: '200px' },
  { key: 'invoice_date', title: t('invoices.invoiceDate'), width: '120px', sortable: true, sortKey: 'invoice_date' },
  { key: 'payment_status', title: t('invoices.paymentStatus'), width: '120px', sortable: true, sortKey: 'payment_status' },
  { key: 'total_amount', title: t('invoices.grandTotal'), width: '180px', sortable: true, sortKey: 'total_amount' },
  { key: 'actions', title: 'Actions', width: '250px', align: 'right' },
]

/**
 * Get payment status badge variant
 */
const getPaymentStatusBadge = (status) => {
  switch (status) {
    case 'paid':
      return 'success'
    case 'partial':
      return 'warning'
    case 'unpaid':
      return 'error'
    default:
      return 'default'
  }
}

/**
 * Get invoice total
 */
const getTotal = (row) => {
  return row.total_amount || row.grand_total || 0
}

/**
 * Get amount paid
 */
const getPaid = (row) => {
  return row.amount_paid || 0
}

/**
 * Get amount due
 */
const getDue = (row) => {
  return getTotal(row) - getPaid(row)
}

/**
 * Handle view invoice
 */
const handleView = (invoice) => {
  router.push(`/invoices/${invoice.id}`)
}

/**
 * Handle edit invoice — fetch full invoice with items, open edit modal
 */
const handleEdit = async (invoice) => {
  try {
    const response = await getInvoice(invoice.id)
    editInvoice.value = response.invoice || response
    showEditModal.value = true
  } catch (error) {
    console.error('Failed to fetch invoice for editing:', error)
  }
}

const handleEditSuccess = () => {
  showEditModal.value = false
  editInvoice.value = null
  fetch()
}

/**
 * Show print preview for an invoice by ID
 */
const showPrintPreview = async (invoiceId) => {
  try {
    const response = await getInvoicePrint(invoiceId)
    const rawData = response.data || response
    printData.value = flattenPrintData(rawData)
    printInvoiceId.value = invoiceId
    isGSTBusiness.value = !!printData.value.business_gstin
    showPrintModal.value = true
  } catch (error) {
    console.error('Failed to fetch invoice print data:', error)
  }
}

/**
 * Handle print invoice — opens preview modal (user prints/downloads from there)
 */
const handlePrint = async (invoice) => {
  await showPrintPreview(invoice.id)
}

// Paper size from billing settings
const { paperSize, savePaperSize } = usePrint()

const loadPaperSizeFromSettings = async () => {
  try {
    const s = await getBillingSettings()
    const data = s?.data || s
    if (data?.paper_size) {
      paperSize.value = data.paper_size
      savePaperSize(data.paper_size)
    }
  } catch { /* use localStorage default */ }
}

// Server-side PDF — fetch PDF blob via apiClient's axios instance
const fetchInvoicePdfBlob = async (invoiceId) => {
  const { apiClient } = await import('@shared/utils/apiClient.js')
  const response = await apiClient.client.get(
    `/v1/invoices/${invoiceId}/pdf?paper_size=${paperSize.value}`,
    { responseType: 'blob' }
  )
  return response.data
}

const handlePrintFromPreview = async () => {
  if (!printInvoiceId.value) return
  try {
    const blob = await fetchInvoicePdfBlob(printInvoiceId.value)
    const url = URL.createObjectURL(blob)
    window.open(url, '_blank')
  } catch (error) {
    console.error('Print failed:', error)
  }
}

const handleDownloadFromPreview = async () => {
  if (!printInvoiceId.value) return
  try {
    const blob = await fetchInvoicePdfBlob(printInvoiceId.value)
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${printData.value?.invoice_number || 'invoice'}.pdf`
    a.click()
    URL.revokeObjectURL(url)
  } catch (error) {
    console.error('Download failed:', error)
  }
}

/**
 * Handle download PDF from list (without opening preview)
 */
const handleDownloadPdf = async (invoice) => {
  try {
    const response = await getInvoicePrint(invoice.id)
    const rawData = response.data || response
    printData.value = flattenPrintData(rawData)
    isGSTBusiness.value = !!printData.value.business_gstin
    showPrintModal.value = true
    // Download will happen from the modal
  } catch (error) {
    console.error('Failed to generate PDF:', error)
  }
}

/**
 * Handle record payment
 */
const handleRecordPayment = (invoice) => {
  selectedInvoice.value = invoice
  showPaymentModal.value = true
}

/**
 * Handle form success — auto-print the newly created invoice
 */
const handleFormSuccess = async (invoiceId) => {
  showCreateModal.value = false
  await fetch()
  if (invoiceId) {
    await showPrintPreview(invoiceId)
  }
}

/**
 * Handle payment form success
 */
const handlePaymentSuccess = async () => {
  showPaymentModal.value = false
  selectedInvoice.value = null
  await fetch()
}

const handleSort = ({ sortBy: newSort, sortOrder: newOrder }) => {
  sortBy.value = newSort
  sortOrder.value = newOrder
  resetAndFetch({ sort_by: newSort, sort_order: newOrder })
}

const searchQuery = ref('')
const handleSearchInput = () => { resetAndFetch({ search: searchQuery.value, sort_by: sortBy.value, sort_order: sortOrder.value }) }
const clearSearch = () => { searchQuery.value = ''; resetAndFetch({ search: '', sort_by: sortBy.value, sort_order: sortOrder.value }) }

watch(saleTypeFilter, (val) => {
  resetAndFetch({ sale_type: val || undefined })
})

const handleNotifications = () => {
  console.log('Notifications')
}

const handleSettings = () => {
  router.push('/settings')
}

const handleLogout = async () => {
  try {
    await logout()
    router.push('/login')
  } catch (error) {
    console.error('Logout error:', error)
    router.push('/login')
  }
}

onMounted(async () => {
  fetchBusinessName()
  loadPaperSizeFromSettings()
  // Handle ?print=ID from InvoiceDetailView
  const printId = route.query.print
  if (printId) {
    await showPrintPreview(printId)
    // Clean up the query param
    router.replace({ query: {} })
  }
})
</script>

<style scoped>
@import '@shared/styles/intercom-tokens.css';
@import '@shared/styles/intercom-base.css';

.invoices-view {
  padding: var(--space-8);
  max-width: var(--content-max-width);
  margin: 0 auto;
}

.page-header {
  margin-bottom: var(--space-8);
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.page-title {
  font-size: var(--font-size-2xl);
  font-weight: var(--font-weight-bold);
  color: var(--color-charcoal);
  letter-spacing: var(--letter-spacing-tight);
  margin: 0;
}

.page-subtitle {
  font-size: var(--font-size-base);
  color: var(--color-gray-500);
  margin-top: var(--space-1);
  margin-bottom: 0;
}

.amount-cell {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
}

.amount-cell .total {
  color: var(--color-charcoal);
  font-size: var(--font-size-sm);
}

.amount-cell .paid {
  font-size: var(--font-size-xs);
  color: #18a058;
}

.amount-cell .due {
  font-size: var(--font-size-xs);
  color: #d03050;
  font-weight: var(--font-weight-medium);
}

.action-buttons {
  display: flex;
  gap: var(--space-2);
  justify-content: flex-end;
}

@media (max-width: 768px) {
  .invoices-view {
    padding: var(--space-4);
  }

  .page-header {
    flex-direction: column;
    align-items: flex-start;
    gap: var(--space-4);
  }

  .page-title {
    font-size: 26px;
  }
}

/* Print Preview */
.print-preview {
  max-height: 80vh;
  overflow-y: auto;
  display: flex;
  justify-content: center;
  padding: 16px 0;
}

.print-preview.preview-a5 > .tax-invoice-pages {
  transform: scale(1.1);
  transform-origin: top center;
  margin-bottom: 40mm;
}

.print-preview.preview-a5 .tax-invoice-print {
  width: 148mm;
  max-width: 148mm;
  margin: 0 auto 16px auto;
  box-shadow: 0 2px 8px rgba(0,0,0,0.15);
}

/* Search Bar */
.search-bar { display: flex; gap: var(--space-3); margin-bottom: var(--space-6); }
.search-input-wrapper { flex: 1; position: relative; display: flex; align-items: center; }
.search-icon { position: absolute; left: 14px; color: var(--color-gray-400); pointer-events: none; }
.search-input { width: 100%; height: 44px; padding: 0 var(--space-10) 0 44px; font-size: var(--font-size-base); color: var(--color-charcoal); background: var(--color-white); border: 1px solid var(--color-gray-200); border-radius: var(--radius-lg); outline: none; transition: all var(--transition-fast); }
.search-input:focus { border-color: var(--color-blue); box-shadow: 0 0 0 3px rgba(0, 87, 255, 0.1); }
.search-clear { position: absolute; right: 12px; display: flex; align-items: center; justify-content: center; width: 24px; height: 24px; background: var(--color-gray-100); border: none; border-radius: 50%; cursor: pointer; color: var(--color-gray-500); transition: all var(--transition-fast); }
.search-clear:hover { background: var(--color-gray-200); color: var(--color-gray-700); }

/* Sale Type Filter */
.filters-row {
  display: flex;
  gap: var(--space-2, 6px);
  margin-bottom: var(--space-4, 12px);
}

/* Sale Type Badge */
.status-cell {
  display: flex;
  flex-direction: row;
  gap: 6px;
  align-items: center;
}

.sale-type-badge {
  font-size: 11px;
  padding: 2px 6px;
  border-radius: 4px;
  font-weight: 500;
}

.sale-type-badge.cash {
  background: var(--surface-subtle, #f0f0f0);
  color: var(--text-muted, #666);
}

.sale-type-badge.credit {
  background: #fff3e0;
  color: #e65100;
}

.print-modal-footer {
  display: flex;
  flex-direction: column;
  gap: var(--space-3, 8px);
  width: 100%;
}

.print-hint {
  font-size: 11px;
  color: var(--color-gray-500, #6b7280);
  margin: 0;
  padding: 4px 8px;
  background: var(--color-gray-50, #f9fafb);
  border-radius: 4px;
  border-left: 3px solid var(--color-blue, #0057ff);
}

.print-modal-actions {
  display: flex;
  gap: var(--space-2, 6px);
  justify-content: flex-end;
}

</style>

<!-- Print/Download handled by server-side PDF: GET /v1/invoices/{id}/pdf -->
